import { ShowToast, ToastConfig } from '../model';
import { ErrorMonitor, Translate } from '../services';
import { PromisesSequence } from './PromisesSequence';
import { EventEmitter } from 'events';
import { TypedEmitter } from '../util';

export interface ExecuteParams<R> {
  submit: () => Promise<R> | undefined;
  onEventuallyUpdated?: () => unknown;
}

export interface CollectionOptimisticSequenceExecutorParams {
  readonly showToast: ShowToast;
  readonly translate: Translate;
  readonly errorMonitor: ErrorMonitor;
  readonly onRollback: (err: unknown) =>
    | (Omit<ToastConfig, 'action'> & {
        action?: Partial<ToastConfig['action']>;
      })
    | undefined;
}

export class CollectionOptimisticSequenceExecutor {
  readonly showToast;
  readonly translate;
  readonly errorMonitor;
  readonly onRollback;

  readonly seq = new PromisesSequence();

  removeToast: (() => void) | null = null;

  readonly events = new EventEmitter() as TypedEmitter<{
    windowClosedDuringSequence: () => unknown;
  }>;

  constructor(params: CollectionOptimisticSequenceExecutorParams) {
    this.showToast = params.showToast;
    this.translate = params.translate;
    this.errorMonitor = params.errorMonitor;
    this.onRollback = params.onRollback;
  }

  removeAndShowToast(toastConfig: ToastConfig) {
    const { removeToast, showToast } = this;
    removeToast?.();
    this.removeToast = showToast(toastConfig).remove;
  }

  async sequence<R>(params: ExecuteParams<R>) {
    const { seq, errorMonitor, translate: t, onRollback } = this;
    const { submit, onEventuallyUpdated } = params;

    try {
      await seq.enqueue(async () => {
        await submit();
        onEventuallyUpdated?.();
      });
    } catch (err) {
      console.error(err);
      errorMonitor.captureException(err);

      const errorToast = onRollback(err);

      if (errorToast) {
        this.removeAndShowToast({
          ...errorToast,
          type: 'ERROR',
          timeout: 'NONE',
          action: {
            uiType: 'LINK',
            text: t('cairo.toast.retry'),
            removeToastOnClick: true,
            ...errorToast.action,
            onClick: () => {
              this.removeToast?.();
              this.removeToast = null;
              errorToast.action?.onClick?.();
            },
          },
        });

        const currentToast = this.removeToast;
        new Promise((resolve) => setTimeout(resolve, 12000)).then(() => {
          if (currentToast === this.removeToast) {
            currentToast?.();
          }
        });
      }
    }
  }
}
